import { useEffect, useState, useCallback } from 'react'
import { useSettings } from './settings'
import { flushPreviousDay } from './journal'
import { useTodayKey, todayKey } from './dateUtils'

const STORAGE_KEY = 'gps-journal-daily-habits'
const CHANGE_EVENT = 'gps-daily-habits-changed'

export interface DailyHabit {
  text: string
  completed: boolean
}

interface DailyHabitsRecord {
  date: string
  habits: DailyHabit[]
}

function loadRaw(): DailyHabitsRecord | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    return JSON.parse(raw) as DailyHabitsRecord
  } catch {
    return null
  }
}

function saveRaw(record: DailyHabitsRecord) {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(record))
    window.dispatchEvent(new CustomEvent(CHANGE_EVENT))
  } catch {
    // ignore
  }
}

/**
 * Shared, reactive store for today's habit completion state.
 * - Reconciles with user's Settings habits list (preserving completion by text).
 * - Resets at midnight: yesterday's record is flushed to the journal log,
 *   then today's record starts fresh (everything unchecked).
 */
export function useDailyHabits() {
  const settings = useSettings()
  const today = useTodayKey()

  const reconcile = useCallback((): DailyHabit[] => {
    const record = loadRaw()
    const sameDay = record && record.date === today
    const prevByText = new Map<string, boolean>(
      sameDay ? record!.habits.map((h) => [h.text, h.completed]) : [],
    )
    return settings.habits.map((text) => ({
      text,
      completed: prevByText.get(text) ?? false,
    }))
  }, [settings.habits, today])

  const [habits, setHabits] = useState<DailyHabit[]>(() => {
    // First flush any stale (different-date) record before we touch storage.
    flushPreviousDay()
    return reconcile()
  })

  // Reconcile when settings.habits OR today (date) changes.
  useEffect(() => {
    flushPreviousDay()
    const next = reconcile()
    setHabits(next)
    saveRaw({ date: todayKey(), habits: next })
  }, [reconcile])

  // Listen for cross-component / cross-tab updates
  useEffect(() => {
    const refresh = () => {
      const record = loadRaw()
      if (record && record.date === todayKey()) {
        setHabits(record.habits)
      }
    }
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) refresh()
    }
    window.addEventListener(CHANGE_EVENT, refresh)
    window.addEventListener('storage', onStorage)
    return () => {
      window.removeEventListener(CHANGE_EVENT, refresh)
      window.removeEventListener('storage', onStorage)
    }
  }, [])

  const toggleHabit = useCallback((text: string) => {
    setHabits((prev) => {
      const next = prev.map((h) =>
        h.text === text ? { ...h, completed: !h.completed } : h,
      )
      saveRaw({ date: todayKey(), habits: next })
      return next
    })
  }, [])

  const completedCount = habits.filter((h) => h.completed).length
  const totalCount = habits.length

  return { habits, toggleHabit, completedCount, totalCount }
}
